import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { GRANICE_DO_BAZY } from '@/lib/skalaCeny';
import { supabase } from '@/integrations/supabase/client';
import { obszaryDoKontraktu } from '@/lib/histogramObszary';
import { serpFilterKey } from '@/hooks/serpQuery';
import { prefsHistogramu } from '@/lib/serpRead/predykatyMiekkie';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { RODZAJE_BEZ_AGENCJI } from '@/lib/bezAgencji';

/**
 * Histogram cen nad suwakiem (etap J, wzór Airbnb).
 *
 * LICZY GO BAZA, nie front — i to nie jest wybór wygody. Front ma najwyżej 1 000 pinów
 * (sufit PostgREST), a histogram ma opisywać CAŁY wynik; policzony z tego, co przyszło,
 * kłamałby tym bardziej, im większy jest wynik. Po stronie bazy liczy go
 * `flat.offers_price_histogram`, nakładka nad `flat.offers_matching` — tym samym predykatem,
 * który stoi za licznikiem „Pokaż N ofert".
 *
 * JEDNO ZAPYTANIE NA ZMIANĘ INNEGO FILTRA, nie na ruch suwaka: klucz zapytania celowo
 * POMIJA cenę. Suwak przesuwa się dziesiątki razy na sekundę, a histogram i tak pokazuje
 * rozkład BEZ filtra ceny — inaczej zawężanie zakresu zjadałoby własne słupki.
 *
 * DZIAŁA PRZED MIGRACJĄ. Dopóki funkcji nie ma w bazie, PostgREST odpowiada 404/42883;
 * traktujemy to jako „histogramu nie ma", a nie jako awarię — arkusz pokazuje sam suwak,
 * bez błędu w konsoli i bez pustej ramki. Dzięki temu front może wejść przed migracją.
 */
/**
 * Koszyk z bazy. Granice są takie, jak wysłane w `p_granice` — czyli przesunięte o 1 zł
 * (`GRANICE_DO_BAZY`); `dol = null` to ogon poniżej skali, `gora = null` — od ostatniej
 * granicy (migracja 20260928112945). Przeliczenie na skalę robi `slupkiNaGranicach`.
 */
export interface KoszykCeny {
  dol: number | null;
  gora: number | null;
  ile: number;
}

export interface HistogramCen {
  koszyki: KoszykCeny[];
  /** Percentyle 1 i 99 — granice, z których można ustawić zakres suwaka. */
  p1: number | null;
  p99: number | null;
  /** Funkcji nie ma jeszcze w bazie — pokazujemy sam suwak. */
  niedostepny: boolean;
}

const PUSTY: HistogramCen = { koszyki: [], p1: null, p99: null, niedostepny: true };

/**
 * Histogram jest OZDOBĄ: gdy baza go nie da, arkusz pokazuje sam suwak i tyle. Traktujemy tak
 * trzy odpowiedzi, wszystkie zmierzone na żywym wdrożeniu:
 *  • 42883 / PGRST202 — funkcji nie ma (front wchodzi przed migracją, tak było zaplanowane);
 *  • 42501 „permission denied" — funkcja jest, ale wołający nie ma dostępu do wszystkiego,
 *    czego dotyka jej ciało. Zdarzyło się od razu po wdrożeniu: `returns setof flat.offers`
 *    wymaga SELECT na WSZYSTKICH kolumnach tabeli, a anon nie ma portalowych (DEC-04).
 *    Interfejs zachował się wtedy poprawnie (suwak działał, zasłony nie było), ale zapytanie
 *    leciało cztery razy zamiast raz, bo błąd szedł do ponowień.
 * Zwracamy więc „niedostępny" ZAMIAST rzucać i wyłączamy ponowienia — ozdoba nie ma prawa
 * dokładać ruchu ani szumu.
 */
function histogramNiedostepny(blad: { code?: string; message?: string } | null): boolean {
  if (!blad) return false;
  if (blad.code === '42883' || blad.code === 'PGRST202' || blad.code === '42501') return true;
  return /does not exist|permission denied/i.test(blad.message ?? '');
}

export function useHistogramCen(
  filters: SourcePostFilters,
  opcje: { enabled?: boolean } = {},
) {
  const tryb = filters.costMode === 'total' ? 'total' : 'rent';
  // Klucz BEZ ceny — patrz nagłówek. `serpFilterKey` normalizuje resztę filtrów, więc
  // histogram odświeża się dokładnie wtedy, gdy zmienia się zbiór, a nie widok na niego.
  const { rentMin: _min, rentMax: _max, costMode: _tryb, ...bezCeny } = filters;
  const klucz = JSON.stringify(serpFilterKey(bezCeny as SourcePostFilters));

  return useQuery<HistogramCen>({
    queryKey: ['flat.histogram_cen', tryb, klucz],
    enabled: opcje.enabled ?? true,
    staleTime: 5 * 60_000,
    // Stare słupki zostają, dopóki nie przyjdą nowe (Piotr 29.09: „przełączanie filtrów powoduje,
    // że pokoje przeskakują”) — bez tego histogram znikał na czas pobrania i wszystko pod nim
    // skakało o 52 px przy każdej zmianie filtra spoza ceny.
    placeholderData: keepPreviousData,
    // Bez ponowień: jedyne błędy, jakie tu wychodzą, to „nie da się" (patrz wyżej), a
    // czterokrotne pytanie o to samo nie zmieni odpowiedzi.
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.schema('flat' as never).rpc('offers_price_histogram' as never, {
        p_tryb: tryb,
        // Granice skali suwaka (przesunięte o 1 zł — patrz skalaCeny.ts). `p_koszyki` już nie
        // wysyłamy: baza go ignoruje, a agent danych usunie go osobną migracją
        // (PreLaunchChecklist 2.7).
        p_granice: GRANICE_DO_BAZY,
        p_areas: obszaryDoKontraktu(bezCeny.areas),
        p_rooms: bezCeny.rooms ?? null,
        p_area_min: bezCeny.areaMin ?? null,
        p_area_max: bezCeny.areaMax ?? null,
        p_near_metro: !!bezCeny.nearMetro,
        p_poi: bezCeny.poi ?? null,
        // Zwierzęta miękko po obu stronach: `flat.offers_matching` (migracja 20260928105401)
        // odcina tylko jawne „nie", tak jak łańcuch listy (`serpQuery.ts`). Zmierzone 28.09:
        // słupki 2 947 + bez ceny 519 = 3 466 = licznik listy z filtrem.
        p_pets: !!bezCeny.zeZwierzeciem,
        p_available_now: !!bezCeny.availableNow,
        // Agencji nie ma nigdzie: stały warunek, jak w łańcuchu listy (src/lib/bezAgencji.ts).
        p_posting_types: RODZAJE_BEZ_AGENCJI,
        p_posted_since_hours: bezCeny.postedSinceHours ?? null,
        // Filtry miękkie (umeblowanie, parter, kuchnia, łazienki; zwierzęta też tu, obok
        // p_pets): TEN SAM obiekt co dla `offers_ranked`, żeby słupki liczyły to, co lista.
        // Bez filtrów miękkich — null, funkcja działa po staremu.
        // Plus filtry twarde znane `offers_matching` tylko z `p_prefs` (właściciel na FlatHero).
        p_prefs: prefsHistogramu(bezCeny),
      } as never);
      if (error) {
        if (histogramNiedostepny(error as { code?: string; message?: string })) return PUSTY;
        throw error;
      }
      const wiersze = (data as unknown as Array<{ dol: number; gora: number; ile: number; p1: number; p99: number }>) ?? [];
      if (wiersze.length === 0) return { koszyki: [], p1: null, p99: null, niedostepny: false };
      return {
        koszyki: wiersze.map((w) => ({ dol: w.dol, gora: w.gora, ile: w.ile })),
        p1: wiersze[0].p1,
        p99: wiersze[0].p99,
        niedostepny: false,
      };
    },
  });
}
